import { EVENT_TYPES, SEVERITIES, type Camera, type VisionEvent } from '@playplate/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { EmptyState, ErrorState, Loading, PageHeader, Panel, SeverityBadge } from '../components/ui';
import { api, query } from '../lib/api';
import { formatDateTime, humanEventType } from '../lib/format';
import { useState } from 'react';

type Filters = {
  eventType: string;
  severity: string;
  cameraId: string;
  hours: number;
};

const DEFAULT_FILTERS: Filters = { eventType: '', severity: '', cameraId: '', hours: 24 };

export function EventsPage() {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);

  const camerasQuery = useQuery({
    queryKey: ['cameras', 'list'],
    queryFn: () => api.get<{ items: Camera[] }>('/cameras'),
  });

  const since = new Date(Date.now() - filters.hours * 3600_000).toISOString();

  // Keyset pagination: the API returns a cursor rather than an offset, so new
  // events arriving mid-scroll cannot duplicate or hide rows.
  const eventsQuery = useInfiniteQuery({
    queryKey: ['events', 'list', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get<{ items: VisionEvent[]; nextCursor: string | null }>(
        `/events${query({
          since,
          eventType: filters.eventType || undefined,
          severity: filters.severity || undefined,
          cameraId: filters.cameraId || undefined,
          cursor: pageParam,
          limit: 50,
        })}`,
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const events = eventsQuery.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div>
      <PageHeader title="Events" subtitle="Everything the system recorded, whether or not it raised an alert." />

      <Panel className="mb-4">
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="label mb-1.5 block">Time range</span>
            <select
              className="input"
              value={filters.hours}
              onChange={(e) => setFilters((f) => ({ ...f, hours: Number(e.target.value) }))}
            >
              <option value={1}>Last hour</option>
              <option value={6}>Last 6 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={72}>Last 3 days</option>
              <option value={168}>Last 7 days</option>
            </select>
          </label>

          <label className="block">
            <span className="label mb-1.5 block">Event type</span>
            <select
              className="input"
              value={filters.eventType}
              onChange={(e) => setFilters((f) => ({ ...f, eventType: e.target.value }))}
            >
              <option value="">All types</option>
              {EVENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {humanEventType(type)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="label mb-1.5 block">Severity</span>
            <select
              className="input"
              value={filters.severity}
              onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))}
            >
              <option value="">All severities</option>
              {SEVERITIES.map((severity) => (
                <option key={severity} value={severity}>
                  {severity}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="label mb-1.5 block">Camera</span>
            <select
              className="input"
              value={filters.cameraId}
              onChange={(e) => setFilters((f) => ({ ...f, cameraId: e.target.value }))}
            >
              <option value="">All cameras</option>
              {(camerasQuery.data?.items ?? []).map((camera) => (
                <option key={camera.id} value={camera.id}>
                  {camera.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Panel>

      {eventsQuery.isLoading ? (
        <Loading label="Loading events" />
      ) : eventsQuery.isError ? (
        <ErrorState message={(eventsQuery.error as Error).message} onRetry={() => void eventsQuery.refetch()} />
      ) : events.length === 0 ? (
        <EmptyState
          title="No events in this window"
          message="Either nothing happened, or the filters are narrower than the data."
        />
      ) : (
        <Panel>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr>
                  <th className="table-header">Time</th>
                  <th className="table-header">Event</th>
                  <th className="table-header">Severity</th>
                  <th className="table-header">Camera / zone</th>
                  <th className="table-header">Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="align-top transition-colors hover:bg-panel-raised/60">
                    <td className="table-cell whitespace-nowrap font-mono text-xs text-muted">
                      {formatDateTime(event.occurredAt)}
                    </td>
                    <td className="table-cell font-medium">{humanEventType(event.eventType)}</td>
                    <td className="table-cell">
                      <SeverityBadge severity={event.severity} />
                    </td>
                    <td className="table-cell text-muted">
                      {event.cameraName ?? 'System'}
                      {event.zoneName ? <span className="block text-xs">{event.zoneName}</span> : null}
                    </td>
                    <td className="table-cell text-xs text-muted">
                      <MetadataSummary metadata={event.metadata} confidence={event.confidence} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {eventsQuery.hasNextPage ? (
            <div className="border-t border-line p-3 text-center">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => void eventsQuery.fetchNextPage()}
                disabled={eventsQuery.isFetchingNextPage}
              >
                {eventsQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
        </Panel>
      )}
    </div>
  );
}

/** Renders the handful of metadata keys that are actually meaningful to a human. */
function MetadataSummary({
  metadata,
  confidence,
}: {
  metadata: Record<string, unknown>;
  confidence: number | null;
}) {
  const parts: string[] = [];
  if (typeof metadata.occupancy === 'number') parts.push(`occupancy ${metadata.occupancy}`);
  if (typeof metadata.threshold === 'number') parts.push(`threshold ${metadata.threshold}`);
  if (typeof metadata.dwellSeconds === 'number') parts.push(`${metadata.dwellSeconds}s present`);
  if (typeof metadata.silentSeconds === 'number') parts.push(`${metadata.silentSeconds}s silent`);
  if (typeof metadata.reason === 'string') parts.push(metadata.reason);
  if (confidence !== null) parts.push(`confidence ${(confidence * 100).toFixed(0)}%`);

  return <>{parts.length > 0 ? parts.join(' · ') : '—'}</>;
}
